/**
 * Client-side ItineraryItemEvent model with computed properties and methods
 */

import type { FlightSegment } from '@elara/domain/models/schemas/flight-schemas';
import type { ItineraryItemEvent } from '@elara/domain/models/schemas/itinerary-item-event-schemas';

import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import { ItineraryItemEventModelSpec } from '@elara/domain/models/TransientModelSpecs';
import { formatPlaceTypeLabel } from '@elara/domain/models/place-category';

import { createLocalModel } from '#firebase/createLocalModel.js';
import { formatTimeInOriginalTimezone } from '#lib/date-utils.js';
import { getEventTypeIcon } from '#lib/itinerary-utils.js';
import { formatPrice } from '#lib/utils.js';

/** All-day events carry a bare `YYYY-MM-DD` date with no time component. */
function isDateOnly(dateTime: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateTime);
}

export const ItineraryItemEventModel = createLocalModel(ItineraryItemEventModelSpec, (data) => ({
  /**
   * Icon name for the event type (for MaterialIcon)
   */
  get iconName(): IconName {
    return getEventTypeIcon(data.type);
  },

  /** Formatted display cost for this event (set by the expansion engine), or undefined when it carries none. */
  get formattedCost(): string | undefined {
    return data.cost ? formatPrice(data.cost.amount, data.cost.currency) : undefined;
  },

  /**
   * Formatted time (e.g. "2:30 PM")
   */
  get formattedTime(): string {
    return isDateOnly(data.dateTime) ? 'All day' : formatTimeInOriginalTimezone(data.dateTime, 'h:mm a');
  },

  /**
   * Formatted date (e.g. "Jan 15")
   */
  get formattedDate(): string {
    return formatTimeInOriginalTimezone(data.dateTime, 'MMM d');
  },

  /**
   * Formatted date and time (e.g. "Jan 15, 2:30 PM"). All-day events have no time component,
   * so they render as the date alone rather than a misleading midnight.
   */
  get formattedDateTime(): string {
    return formatTimeInOriginalTimezone(data.dateTime, isDateOnly(data.dateTime) ? 'MMM d' : 'MMM d, h:mm a');
  },

  /**
   * Formatted time from the original hotel policy datetime (before flight-relative adjustment).
   * Falls back to formattedTime if no original datetime is stored.
   */
  get originalFormattedTime(): string | undefined {
    const originalDateTime = (data.metadata as { originalDateTime?: string } | undefined)?.originalDateTime;
    if (originalDateTime) {
      return isDateOnly(originalDateTime) ? undefined : formatTimeInOriginalTimezone(originalDateTime, 'h:mm a');
    }
    return this.formattedTime;
  },

  /**
   * Whether this event is a layover (displayed inline, not as a block)
   */
  get isLayover(): boolean {
    return data.type === 'layover';
  },

  /**
   * Whether this event is a flight-related event (departure, arrival, or layover)
   */
  get isFlightEvent(): boolean {
    return data.type === 'flight_departure' || data.type === 'flight_arrival' || data.type === 'layover';
  },

  /**
   * Whether this is the earliest `flight_departure` leg of its item. Booking status is attached to
   * every leg of a multi-segment flight, but a primary action like "Book" should still appear on only
   * one card — callers gate that on this instead of on `bookingStatus`/`cost` presence.
   */
  get isFirstFlightSegment(): boolean {
    return (data.metadata as { isFirstSegment?: boolean } | undefined)?.isFirstSegment === true;
  },

  /**
   * What the place actually is, from the Google primary type the expansion engine denormalizes onto a
   * place event ("Irish pub", "Art gallery") — undefined for non-place events, for places whose only
   * type is one of Google's catch-alls, and for events expanded before this was carried, so callers
   * fall back to the broad category label.
   */
  get placeTypeLabel(): string | undefined {
    const placePrimaryType = (data.metadata as { placePrimaryType?: unknown } | undefined)?.placePrimaryType;
    return typeof placePrimaryType === 'string' ? formatPlaceTypeLabel(placePrimaryType) : undefined;
  },

  get formattedType(): string {
    switch (data.type) {
      case 'flight_departure': {
        const segment = data.metadata?.segment as FlightSegment;
        const airlineName = segment.airline.name;

        return airlineName ?? 'Flight';
      }
      case 'flight_arrival': {
        return 'Arrival';
      }
      case 'layover': {
        return 'Layover';
      }
      case 'hotel_checkin': {
        return 'Check-in';
      }
      case 'hotel_checkout': {
        return 'Check-out';
      }
      case 'activity':
      case 'custom': {
        return 'Activity';
      }
      // A place is named by what it actually is — "Irish pub", "Art gallery" — from the primary type
      // denormalized onto the event (see `ExpandableItem.placePrimaryType`). The broad category is the
      // fallback for places with no usable type and for events expanded before that was carried.
      case 'place': {
        return this.placeTypeLabel ?? 'Visit';
      }
      case 'place_dining': {
        return this.placeTypeLabel ?? 'Dining';
      }
      case 'place_culture': {
        return this.placeTypeLabel ?? 'Culture';
      }
      case 'place_entertainment': {
        return this.placeTypeLabel ?? 'Entertainment';
      }
      case 'place_nature': {
        return this.placeTypeLabel ?? 'Nature';
      }
      case 'place_shopping': {
        return this.placeTypeLabel ?? 'Shopping';
      }
      case 'place_wellness': {
        return this.placeTypeLabel ?? 'Wellness';
      }
      case 'place_nightlife': {
        return this.placeTypeLabel ?? 'Nightlife';
      }
      case 'destination_arrival': {
        const segment = data.metadata?.segment as FlightSegment;
        const countryName = segment.destination.country_name;

        return countryName ? `Welcome to ${countryName}` : 'Welcome';
      }
    }
  },
}));

export type ItineraryItemEventModel = ReturnType<(typeof ItineraryItemEventModel)['from']>;

/**
 * Creates a client-side ItineraryItemEvent instance with computed properties and methods
 */
export function createItineraryItemEventModel(data: ItineraryItemEvent): ItineraryItemEventModel {
  return ItineraryItemEventModel.from(data);
}
